/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, useCallback, useEffect, useState, HTMLAttributes } from 'preact/compat';
import { VNode } from 'preact';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button, Input, Modal, InLineAlert } from '@adobe-commerce/elsie/components';
import { WarningFilled } from '@adobe-commerce/elsie/icons';
import '@/quote-management/components/ReferenceDocumentFormModal/ReferenceDocumentFormModal.css';

export interface ReferenceDocument {
  uid?: string;
  name: string;
  identifier?: string;
  url: string;
}

export interface ReferenceDocumentFormModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  document?: ReferenceDocument;
  /**
   * Optional error message string. If provided, automatically creates an InLineAlert error banner.
   * If both errorMessage and errorBanner are provided, errorBanner takes precedence.
   */
  errorMessage?: string;
  /**
   * Optional custom error banner VNode. Provides full control over error banner rendering.
   * If both errorMessage and errorBanner are provided, errorBanner takes precedence.
   */
  errorBanner?: VNode;
  successBanner?: VNode;
  showCloseButton?: boolean;
  isSubmitting?: boolean;
  onSave: (document: ReferenceDocument) => void;
  onClose?: () => void;
}

export const ReferenceDocumentFormModal: FunctionComponent<ReferenceDocumentFormModalProps> = ({
  className,
  open,
  document,
  errorMessage,
  errorBanner,
  successBanner,
  showCloseButton = true,
  isSubmitting = false,
  onSave,
  onClose,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [documentIdentifier, setDocumentIdentifier] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [errors, setErrors] = useState<{
    documentName?: string;
    documentIdentifier?: string;
    referenceUrl?: string;
  }>({});

  const dictionary = useText({
    title: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.title',
    documentNameLabel: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.documentNameLabel',
    documentIdentifierLabel: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.documentIdentifierLabel',
    referenceUrlLabel: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.referenceUrlLabel',
    addButton: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.addButton',
    updateButton: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.updateButton',
    cancelButton: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.cancelButton',
    documentNameRequired: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.documentNameRequired',
    documentIdentifierRequired: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.documentIdentifierRequired',
    referenceUrlRequired: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.referenceUrlRequired',
    invalidUrl: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.invalidUrl',
    errorHeading: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.errorHeading',
  });

  // Create error banner from errorMessage if errorBanner is not provided
  const displayErrorBanner = errorBanner || (errorMessage ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.errorHeading}
      description={errorMessage}
      data-testid="reference-document-form-modal-error-banner-inline"
    />
  ) : null);

  // Determine if we're in edit mode (document prop is provided)
  const isEditMode = !!document;

  // Reset form when modal opens or document changes
  useEffect(() => {
    if (open) {
      if (document) {
        setDocumentName(document.name || '');
        setDocumentIdentifier(document.identifier || '');
        setReferenceUrl(document.url || '');
      } else {
        setDocumentName('');
        setDocumentIdentifier('');
        setReferenceUrl('');
      }
      setErrors({});
    }
  }, [open, document]);

  const validateUrl = useCallback((url: string): boolean => {
    // istanbul ignore if - whitespace-only URL are caught before this function is called, but leaving this here for defensive programming.
    if (!url.trim()) {
      return false;
    }
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleSave = useCallback(() => {
    const newErrors: { documentName?: string; documentIdentifier?: string; referenceUrl?: string } = {};

    if (!documentName.trim()) {
      newErrors.documentName = dictionary.documentNameRequired;
    }

    if (!referenceUrl.trim()) {
      newErrors.referenceUrl = dictionary.referenceUrlRequired;
    } else if (!validateUrl(referenceUrl.trim())) {
      newErrors.referenceUrl = dictionary.invalidUrl;
    }

    if (!documentIdentifier.trim()) {
      newErrors.documentIdentifier = dictionary.documentIdentifierRequired;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      uid: document?.uid,
      name: documentName.trim(),
      identifier: documentIdentifier.trim(),
      url: referenceUrl.trim(),
    });
  }, [documentName, documentIdentifier, referenceUrl, document, dictionary, validateUrl, onSave]);

  const handleClose = useCallback(() => {
    setDocumentName('');
    setDocumentIdentifier('');
    setReferenceUrl('');
    setErrors({});
    onClose?.();
  }, [onClose]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      size="medium"
      title={<>{dictionary.title}</>}
      onClose={handleClose}
      clickToDismiss={true}
      escapeToDismiss={true}
      showCloseButton={showCloseButton}
      className={classes(['quote-management-reference-document-form-modal', className])}
      data-testid="reference-document-form-modal"
    >
      {displayErrorBanner && (
        <div
          className={classes(['quote-management-reference-document-form-modal__error-banner'])}
          data-testid="reference-document-form-modal-error-banner"
        >
          {displayErrorBanner}
        </div>
      )}

      {successBanner && (
        <div
          className={classes(['quote-management-reference-document-form-modal__success-banner'])}
          data-testid="reference-document-form-modal-success-banner"
        >
          {successBanner}
        </div>
      )}

      <div className={classes(['quote-management-reference-document-form-modal__content'])}>
        <div>
          <Input
            name="documentName"
            type="text"
            value={documentName}
            onInput={(e: any) => {
              setDocumentName(e.target.value);
              setErrors({ ...errors, documentName: undefined });
            }}
            floatingLabel={dictionary.documentNameLabel}
            required
            error={!!errors.documentName}
            disabled={isSubmitting}
            data-testid="reference-document-name-input"
          />
          {errors.documentName && (
            <span
              className={classes(['quote-management-reference-document-form-modal__error-text'])}
              data-testid="reference-document-name-error"
            >
              {errors.documentName}
            </span>
          )}
        </div>

        <div>
          <Input
            name="documentIdentifier"
            type="text"
            value={documentIdentifier}
            onInput={(e: any) => {
              setDocumentIdentifier(e.target.value);
              setErrors({ ...errors, documentIdentifier: undefined });
            }}
            floatingLabel={dictionary.documentIdentifierLabel}
            error={!!errors.documentIdentifier}
            disabled={isSubmitting}
            data-testid="reference-document-identifier-input"
          />
          {errors.documentIdentifier && (
            <span
              className={classes(['quote-management-reference-document-form-modal__error-text'])}
              data-testid="reference-document-identifier-error"
            >
              {errors.documentIdentifier}
            </span>
          )}
        </div>

        <div>
          <Input
            name="referenceUrl"
            type="url"
            value={referenceUrl}
            onInput={(e: any) => {
              setReferenceUrl(e.target.value);
              setErrors({ ...errors, referenceUrl: undefined });
            }}
            floatingLabel={dictionary.referenceUrlLabel}
            required
            error={!!errors.referenceUrl}
            disabled={isSubmitting}
            data-testid="reference-document-url-input"
          />
          {errors.referenceUrl && (
            <span
              className={classes(['quote-management-reference-document-form-modal__error-text'])}
              data-testid="reference-document-url-error"
            >
              {errors.referenceUrl}
            </span>
          )}
        </div>
      </div>

      <div className={classes(['quote-management-reference-document-form-modal__actions'])}>
        <Button
          variant="secondary"
          size="medium"
          onClick={handleClose}
          disabled={isSubmitting}
          className={classes(['quote-management-reference-document-form-modal__cancel-button'])}
          data-testid="reference-document-form-cancel-button"
        >
          {dictionary.cancelButton}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleSave}
          disabled={isSubmitting}
          className={classes(['quote-management-reference-document-form-modal__save-button'])}
          data-testid="reference-document-form-save-button"
        >
          {isEditMode ? dictionary.updateButton : dictionary.addButton}
        </Button>
      </div>
    </Modal>
  );
};

